import { Modal } from 'components/Modal';
import { createContext, useCallback, useState } from 'react';
import DropArea from './DropArea';
import { Flex, notification } from 'antd';
import ChargesReview from './ChargesReview';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ImportsService } from 'modules/escrow/services';
import { useParams } from 'react-router-dom';

export interface IImportCsvModalProps {
	type: 'destination' | 'transfer';
	isOpen: boolean;
	onClose: () => void;
}

export interface IUploadCsvProviderContext {
	step: UploadCsvSteps;
	file?: File | null;
	type: 'destination' | 'transfer';
	isPending: boolean;
	errorMessage: string | null;
	setStep: (step: UploadCsvSteps) => void;
	onClose: () => void;
	handleConfirmImport: () => void;
	clearError: () => void;
}

export const IMPORT_ERROR_FALLBACK_MESSAGE =
	'Não foi possível processar a importação. Tente novamente em alguns instantes.';

/**
 * `NetworkApi` rejeita com `error.response` (e não com o `AxiosError`), então
 * cobrimos os dois formatos. O `message` do backend já vem pronto para o
 * usuário final e é renderizado como veio.
 */
const extractImportErrorMessage = (error: any): string =>
	error?.response?.data?.message ??
	error?.data?.message ??
	IMPORT_ERROR_FALLBACK_MESSAGE;

const extractImportErrorCode = (error: any): string | undefined =>
	error?.response?.data?.code ?? error?.data?.code;

/**
 * Só erros explicativos — que exigem uma ação concreta do usuário sobre o
 * arquivo — ganham o banner dentro do modal. Os demais seguem no toast.
 */
const BANNER_ERROR_CODES = ['csv_import.too_many_pix_lines'];

export enum UploadCsvSteps {
	UPLOAD = 'UPLOAD',
	REVIEW = 'REVIEW',
}

const ModalMaxSize = {
	[UploadCsvSteps.UPLOAD]: 500,
	[UploadCsvSteps.REVIEW]: 500,
};

export const UploadCsvProviderContext =
	createContext<IUploadCsvProviderContext>({
		step: UploadCsvSteps.UPLOAD,
		file: null,
		type: 'destination',
		isPending: false,
		errorMessage: null,
		setStep: () => {
			throw new Error('Method not implemented.');
		},
		onClose: () => {
			throw new Error('Method not implemented.');
		},
		handleConfirmImport: () => {
			throw new Error('Method not implemented.');
		},
		clearError: () => {
			throw new Error('Method not implemented.');
		},
	});

const ImportCsvModal = ({ type, isOpen, onClose }: IImportCsvModalProps) => {
	const { id } = useParams();
	const queryClient = useQueryClient();
	const [api, contextHolder] = notification.useNotification();

	const [step, setStep] = useState(UploadCsvSteps.UPLOAD);
	const [file, setFile] = useState<File | null>(null);
	const [loadingPercentage, setLoadingPercentage] = useState(0);
	const [loading, setLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const clearError = useCallback(() => {
		setErrorMessage(null);
	}, []);

	const handleDrop = useCallback(
		(uploadedFile: File) => {
			clearError();
			setFile(uploadedFile);
		},
		[clearError],
	);

	const { mutate: createImport, isPending } = useMutation({
		mutationFn: (payload: {
			file: File;
			type: 'destination' | 'transfer';
			accountId: string;
		}) => ImportsService.createImport(payload),

		onSuccess: () => {
			api.success({
				message: 'Importação criada com sucesso',
			});

			queryClient.invalidateQueries({
				queryKey: ['imports', type],
			});

			handleClose();
		},

		onError: (e: any, variables) => {
			const message = extractImportErrorMessage(e);

			// O banner só existe na etapa de revisão (fluxo de transferência) e
			// só para os erros explicativos. Todo o resto continua no toast.
			const showInBanner =
				variables.type === 'transfer' &&
				BANNER_ERROR_CODES.includes(extractImportErrorCode(e) ?? '');

			if (showInBanner) {
				setErrorMessage(message);
			} else {
				api.error({
					message: 'Erro ao criar importação',
					description: message,
				});
			}

			setLoading(false);
			setLoadingPercentage(0);
		},
	});

	const handleSetLoadingFalse = useCallback(async () => {
		return new Promise<void>(resolve => {
			setTimeout(() => {
				setLoading(false);
				resolve();
			}, 500);
		});
	}, []);

	const doUpload = useCallback(
		(uploadFile: File, uploadType: 'destination' | 'transfer') => {
			if (!id) return;

			clearError();
			setLoading(true);
			setLoadingPercentage(0);

			const simulateProgress = setInterval(() => {
				setLoadingPercentage(prev => {
					if (prev >= 90) {
						clearInterval(simulateProgress);
						return 90;
					}
					return prev + 10;
				});
			}, 150);

			createImport(
				{ file: uploadFile, type: uploadType, accountId: id },
				{
					onSettled: () => {
						clearInterval(simulateProgress);
						setLoadingPercentage(100);
						handleSetLoadingFalse();
					},
				},
			);
		},
		[id, createImport, handleSetLoadingFalse, clearError],
	);

	const handleNext = useCallback(() => {
		if (!file) return;

		if (type === 'transfer') {
			setStep(UploadCsvSteps.REVIEW);
			return;
		}

		doUpload(file, 'destination');
	}, [file, type, doUpload]);

	const handleConfirmImport = useCallback(() => {
		if (!file || !id) return;
		doUpload(file, 'transfer');
	}, [file, id, doUpload]);

	const handleClose = useCallback(() => {
		setFile(null);
		setStep(UploadCsvSteps.UPLOAD);
		setLoadingPercentage(0);
		setLoading(false);
		clearError();
		onClose();
	}, [onClose, clearError]);

	const getTitle = () => {
		if (step === UploadCsvSteps.REVIEW) return 'Confirmação';
		return `Importar ${
			type === 'destination'
				? 'cadastro de contas beneficiárias'
				: 'transferências'
		} em lote`;
	};

	return (
		<>
			{contextHolder}
			<Modal
				title={getTitle()}
				isOpen={isOpen}
				onClose={handleClose}
				width={ModalMaxSize[step]}
			>
				<Flex justify="center" align="center">
					<UploadCsvProviderContext.Provider
						value={{
							step,
							file,
							type,
							isPending,
							errorMessage,
							setStep,
							onClose: handleClose,
							handleConfirmImport,
							clearError,
						}}
					>
						{step === UploadCsvSteps.UPLOAD && (
							<DropArea
								onUpload={handleDrop}
								removeFile={() => {
									clearError();
									setFile(null);
								}}
								file={file}
								onNext={handleNext}
								loading={loading}
								loadPercentage={loadingPercentage}
							/>
						)}
						{step === UploadCsvSteps.REVIEW && <ChargesReview />}
					</UploadCsvProviderContext.Provider>
				</Flex>
			</Modal>
		</>
	);
};

export default ImportCsvModal;
